import { useEffect, useRef, useState, useCallback } from "react";

/**
 * Custom hook to analyze audio from an HTMLAudioElement using Web Audio API
 * Returns real-time voice energy (0-1) for animation synchronization
 */
export default function useVoiceEnergy() {
  const [voiceEnergy, setVoiceEnergy] = useState(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const animationRef = useRef(null);
  const dataArrayRef = useRef(null);
  const connectedElementRef = useRef(null);
  const voiceEnergyRef = useRef(0);
  const stateUpdateCounterRef = useRef(0);
  
  // Connect an audio element to the analyzer
  const connectAudio = useCallback((audioElement) => {
    if (!audioElement || connectedElementRef.current === audioElement) {
      return;
    }
    
    try {
      // Clean up previous connections
      cleanup();
      
      // Create AudioContext (lazy initialization)
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      
      // Resume context if suspended (browser autoplay policy)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      // Create analyser node
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.7;
      analyserRef.current = analyser;
      
      // Create source from audio element
      const sourceNode = audioContext.createMediaElementSource(audioElement);
      sourceNodeRef.current = sourceNode;
      
      // Connect: source -> analyser -> destination (speakers)
      sourceNode.connect(analyser);
      analyser.connect(audioContext.destination);
      
      // Create data array for frequency analysis
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      dataArrayRef.current = dataArray;
      
      connectedElementRef.current = audioElement;
      setIsAnalyzing(true);
      
      // Start the analysis loop
      startAnalysis();
      
    } catch (err) {
      console.error("Error connecting audio for analysis:", err);
      setIsAnalyzing(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Calculate RMS (Root Mean Square) energy from frequency data
  const calculateEnergy = useCallback(() => {
    if (!analyserRef.current || !dataArrayRef.current) {
      return 0;
    }
    
    // Get frequency data
    analyserRef.current.getByteFrequencyData(dataArrayRef.current);
    
    // Calculate RMS energy
    let sum = 0;
    const data = dataArrayRef.current;
    const length = data.length;
    
    // Focus on voice frequencies (roughly 85-255 Hz range, indices ~2-30 for fftSize 256)
    const voiceStartBin = 2;
    const voiceEndBin = Math.min(40, length);
    
    for (let i = voiceStartBin; i < voiceEndBin; i++) {
      const value = data[i] / 255;
      sum += value * value;
    }
    
    const rms = Math.sqrt(sum / (voiceEndBin - voiceStartBin));
    
    // Apply strong boost for better visual response
    const boostedEnergy = Math.min(1, rms * 3.5);
    
    return boostedEnergy;
  }, []);
  
  // Analysis loop — update ref every frame, setState every ~6 frames (~10fps)
  const startAnalysis = useCallback(() => {
    const analyze = () => {
      const energy = calculateEnergy();
      voiceEnergyRef.current = energy;
      stateUpdateCounterRef.current++;
      if (stateUpdateCounterRef.current % 6 === 0) {
        setVoiceEnergy(energy);
      }
      animationRef.current = requestAnimationFrame(analyze);
    };
    
    analyze();
  }, [calculateEnergy]);
  
  // Stop analysis and clean up
  const cleanup = useCallback(() => {
    // Stop animation frame
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    
    // Disconnect source node (but don't close context yet to avoid issues)
    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.disconnect();
      } catch (e) {
        // Ignore - may already be disconnected
      }
      sourceNodeRef.current = null;
    }
    
    // Disconnect analyser
    if (analyserRef.current) {
      try {
        analyserRef.current.disconnect();
      } catch (e) {
        // Ignore
      }
      analyserRef.current = null;
    }
    
    dataArrayRef.current = null;
    connectedElementRef.current = null;
  }, []);
  
  // Disconnect audio element
  const disconnectAudio = useCallback(() => {
    cleanup();
    
    // Close audio context
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    
    voiceEnergyRef.current = 0;
    setVoiceEnergy(0);
    setIsAnalyzing(false);
  }, [cleanup]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, [cleanup]);
  
  return {
    voiceEnergy,
    voiceEnergyRef,
    isAnalyzing,
    connectAudio,
    disconnectAudio
  };
}
